import { EFormControl } from '@common/enums/form-control.enum';
import { FormAddress } from '@common/general-ui/address/form-address.component';
import { FormCheckboxGroup } from '@common/general-ui/checkbox-group/form-checkbox-group.component';
import { FormCheckboxV2 } from '@common/general-ui/checkbox-v2/form-checkbox-v2.component';
import { FormCheckbox } from '@common/general-ui/checkbox/form-checkbox.component';
import { FormDatePicker } from '@common/general-ui/date-picker/form-date-picker.component';
import { FormGetAddressIO } from '@common/general-ui/get-address-io/form-get-address-io.component';
import { FormInputLoqate } from '@common/general-ui/input-loqate/form-input-loqate.component';
import { FormInputV2 } from '@common/general-ui/input-v2/form-input-v2.component';
import { FormInput as FormInput } from '@common/general-ui/input/form-input.component';
import { FormMultiSelectV2 } from '@common/general-ui/multi-select-v2/form-multi-select-v2.component';
import { FormMultiSelect } from '@common/general-ui/multi-select/form-multi-select.component';
import { FormORSwitch } from '@common/general-ui/or-switch/form-or-switch.component';
import { FormPasswordV2 } from '@common/general-ui/password-v2/form-password-v2.component';
import { FormPassword } from '@common/general-ui/password/form-password.component';
import { FormPhoneNumberV2 } from '@common/general-ui/phone-number-v2/form-phone-number-v2.component';
import { FormRadioGroup } from '@common/general-ui/radio-group/form-radio-group.component';
import { IRadioGroupProps } from '@common/general-ui/radio-group/radio-group.component';
import { FormRangePicker } from '@common/general-ui/range-picker/form-range-picker.component';
import { FormSelectV2 } from '@common/general-ui/select-v2/form-select-v2.component';
import { FormSelect } from '@common/general-ui/select/form-select.component';
import { FormSwitch } from '@common/general-ui/switch/form-switch.component';
import { FormTextAreaV2 } from '@common/general-ui/textarea-v2/form-textarea-v2.component';
import { FormTextArea } from '@common/general-ui/textarea/form-textarea.component';
import { FormTimePicker } from '@common/general-ui/time-picker/form-time-picker.component';
import { FormTransfer } from '@common/general-ui/transfer/form-transfer.component';
import { FormUploadImage } from '@common/general-ui/upload-image/form-upload-image.component';
import { IFormFieldProps } from '@common/interfaces/form-field-props.interface';
import React, { useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import { FormPhoneNumber } from '@common/general-ui/phone-number/form-phone-number.component';

type IProps<TFormValues> = {
    className?: string;
    name: keyof TFormValues;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onFieldChange?: (name: keyof TFormValues, value: any) => void;
    onFieldBlur?: (name: keyof TFormValues) => void;
    maxLength?: number;
} & IFormFieldProps<keyof TFormValues>;

export function FormField<TFormValues>(props: IProps<TFormValues>): JSX.Element {
    const { onFieldChange, onFieldBlur, control, name, className, maxLength, readOnly, ...rest } = props;
    const { watch } = useFormContext();

    const handleOnBlur = useCallback(() => {
        onFieldBlur && onFieldBlur(name);
    }, [name, onFieldBlur]);

    const handleOnChange = useCallback(() => {
        onFieldChange && onFieldChange(name, watch(name as string));
    }, [name, onFieldChange, watch]);

    const renderContent = (): JSX.Element => {
        switch (control) {
            case EFormControl.text:
                return (
                    <FormInput<TFormValues>
                        type="text"
                        className={className}
                        name={name}
                        onChange={handleOnChange}
                        onBlur={handleOnBlur}
                        maxLength={maxLength}
                        {...rest}
                        readOnly={readOnly}
                    />
                );
            case EFormControl.textV2:
                return (
                    <FormInputV2<TFormValues>
                        type="text"
                        className={className}
                        name={name}
                        onChange={handleOnChange}
                        onBlur={handleOnBlur}
                        maxLength={maxLength}
                        {...rest}
                        readOnly={readOnly}
                    />
                );
            case EFormControl.phoneNumber:
              return (
                <FormPhoneNumber<TFormValues>
                    type="text"
                    className={className}
                    name={name}
                    onChange={handleOnChange}
                    onBlur={handleOnBlur}
                    {...rest}
                    readOnly={readOnly}
                />
            );
            case EFormControl.phoneNumberV2:
                return (
                    <FormPhoneNumberV2<TFormValues>
                        type="text"
                        className={className}
                        name={name}
                        onChange={handleOnChange}
                        onBlur={handleOnBlur}
                        {...rest}
                        readOnly={readOnly}
                    />
                );
            case EFormControl.password:
                return (
                    <FormPassword<TFormValues>
                        type="password"
                        className={className}
                        name={name}
                        onChange={handleOnChange}
                        onBlur={handleOnBlur}
                        {...rest}
                        readOnly={readOnly}
                    />
                );
            case EFormControl.passwordV2:
                return (
                    <FormPasswordV2<TFormValues>
                        type="password"
                        className={className}
                        name={name}
                        onChange={handleOnChange}
                        onBlur={handleOnBlur}
                        {...rest}
                        readOnly={readOnly}
                    />
                );

            case EFormControl.textArea:
                return (
                    <FormTextArea<TFormValues>
                        className={className}
                        name={name}
                        onChange={handleOnChange}
                        onBlur={handleOnBlur}
                        maxLength={maxLength}
                        {...rest}
                        readOnly={readOnly}
                    />
                );
            case EFormControl.textAreaV2:
                return (
                    <FormTextAreaV2<TFormValues>
                        className={className}
                        name={name}
                        onChange={handleOnChange}
                        onBlur={handleOnBlur}
                        maxLength={maxLength}
                        {...rest}
                        readOnly={readOnly}
                    />
                );
            case EFormControl.checkbox:
                return (
                    <FormCheckbox<TFormValues>
                        className={className}
                        name={name}
                        onChange={handleOnChange}
                        onBlur={handleOnBlur}
                        {...rest}
                        readOnly={readOnly}
                    />
                );
            case EFormControl.checkboxV2:
                return (
                    <FormCheckboxV2<TFormValues>
                        className={className}
                        name={name}
                        onChange={handleOnChange}
                        onBlur={handleOnBlur}
                        {...rest}
                        readOnly={readOnly}
                    />
                );
            case EFormControl.checkboxGroup:
                return (
                    <FormCheckboxGroup<TFormValues>
                        className={className}
                        name={name}
                        onChange={handleOnChange}
                        onBlur={handleOnBlur}
                        {...rest}
                        readOnly={readOnly}
                    />
                );
            case EFormControl.switch:
                return (
                    <FormSwitch<TFormValues>
                        className={className}
                        name={name}
                        onChange={handleOnChange}
                        onBlur={handleOnBlur}
                        {...rest}
                        readOnly={readOnly}
                    />
                );
            case EFormControl.orSwitch:
                return (
                    <FormORSwitch<TFormValues>
                        className={className}
                        name={name}
                        onChange={handleOnChange}
                        onBlur={handleOnBlur}
                        childrenRight=""
                        childrenLeft=""
                        {...rest}
                        readOnly={readOnly}
                    />
                );
            case EFormControl.numeric:
                return (
                    <FormInput<TFormValues>
                        className={className}
                        type="number"
                        name={name}
                        onChange={handleOnChange}
                        onBlur={handleOnBlur}
                        {...rest}
                        readOnly={readOnly}
                    />
                );
            case EFormControl.numericV2:
                return (
                    <FormInputV2<TFormValues>
                        className={className}
                        type="number"
                        name={name}
                        onChange={handleOnChange}
                        onBlur={handleOnBlur}
                        {...rest}
                        readOnly={readOnly}
                    />
                );
            case EFormControl.email:
                return (
                    <FormInput<TFormValues>
                        className={className}
                        type="email"
                        name={name}
                        onChange={handleOnChange}
                        onBlur={handleOnBlur}
                        {...rest}
                        readOnly={readOnly}
                    />
                );
            case EFormControl.select:
                return (
                    <FormSelect<TFormValues>
                        className={className}
                        name={name}
                        {...rest}
                        onChange={handleOnChange}
                        onBlur={handleOnBlur}
                        readOnly={readOnly}
                    />
                );
            case EFormControl.selectV2:
                return (
                    <FormSelectV2<TFormValues>
                        className={className}
                        name={name}
                        {...rest}
                        onChange={handleOnChange}
                        onBlur={handleOnBlur}
                        readOnly={readOnly}
                    />
                );
            case EFormControl.treeSelect:
                return (
                    //FormTreeSelect problem with build
                    <FormSelect<TFormValues>
                        className={className}
                        name={name}
                        {...rest}
                        onChange={handleOnChange}
                        onBlur={handleOnBlur}
                        readOnly={readOnly}
                    />
                );
            case EFormControl.multiSelect:
                return (
                    <FormMultiSelect<TFormValues>
                        className={className}
                        name={name}
                        {...rest}
                        onChange={handleOnChange}
                        onBlur={handleOnBlur}
                        readOnly={readOnly}
                    />
                );
            case EFormControl.multiSelectV2:
                return (
                    <FormMultiSelectV2<TFormValues>
                        className={className}
                        name={name}
                        {...rest}
                        onChange={handleOnChange}
                        onBlur={handleOnBlur}
                        readOnly={readOnly}
                    />
                );
            case EFormControl.radioGroup:
                return (
                    <FormRadioGroup<TFormValues>
                        className={className}
                        name={name}
                        onChange={handleOnChange}
                        onBlur={handleOnBlur}
                        {...(rest as unknown as Omit<IRadioGroupProps<TFormValues>, 'name'>)}
                        readOnly={readOnly}
                    />
                );

            case EFormControl.uploadImage:
                return (
                    <FormUploadImage<TFormValues>
                        className={className}
                        name={name}
                        onChange={handleOnChange}
                        onBlur={handleOnBlur}
                        {...rest}
                        readOnly={readOnly}
                    />
                );

            case EFormControl.datePicker:
                return (
                    <FormDatePicker<TFormValues>
                        className={className}
                        name={name}
                        onChange={handleOnChange}
                        onBlur={handleOnBlur}
                        {...rest}
                        readOnly={readOnly}
                    />
                );

            case EFormControl.rangePicker:
                return (
                    <FormRangePicker<TFormValues>
                        className={className}
                        name={name}
                        onChange={handleOnChange}
                        onBlur={handleOnBlur}
                        {...rest}
                        readOnly={readOnly}
                    />
                );

            case EFormControl.inputLoqate:
                return (
                    <FormInputLoqate<TFormValues>
                        className={className}
                        name={name}
                        onChange={handleOnChange}
                        onBlur={handleOnBlur}
                        {...rest}
                        readOnly={readOnly}
                    />
                );

            case EFormControl.getAddressIO:
                return (
                    <FormGetAddressIO<TFormValues>
                        className={className}
                        name={name}
                        onChange={handleOnChange}
                        onBlur={handleOnBlur}
                        {...rest}
                        readOnly={readOnly}
                    />
                );
            case EFormControl.address:
                return (
                    <FormAddress<TFormValues>
                        className={className}
                        name={name}
                        onChange={handleOnChange}
                        onBlur={handleOnBlur}
                        {...rest}
                        readOnly={readOnly}
                    />
                );
            case EFormControl.timePicker:
                return (
                    <FormTimePicker<TFormValues>
                        className={className}
                        name={name}
                        onChange={handleOnChange}
                        onBlur={handleOnBlur}
                        {...rest}
                        readOnly={readOnly}
                    />
                );
            case EFormControl.transfer:
                return (
                    <FormTransfer<TFormValues>
                        className={className}
                        name={name}
                        onChange={handleOnChange}
                        onBlur={handleOnBlur}
                        {...rest}
                        readOnly={readOnly}
                    />
                );

            default:
                return <div>not implemented control={control}</div>;
        }
    };

    return <>{renderContent()}</>;
}
